import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const fmt = (value, decimals = 0) => {
  const num = Number(value || 0);
  return (Number.isNaN(num) ? 0 : num).toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/** Chiffres du cercle (temps, dépenses, vélocité) : repliés derrière une ligne de résumé. */
const CircleMetrics = ({
  metricsRange,
  metricsRangeValid,
  onMetricsRangeChange,
  currentSlotRange,
  isUsingSlotRange,
  summary,
  loading,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const data = summary || { hours: 0, expenses: 0, projects: 0, covered: 0, velocity: 0 };
  const period = isUsingSlotRange ? 'slot en cours' : 'période choisie';
  const line = data.projects === 0
    ? 'aucun projet rattaché'
    : loading
      ? 'calcul en cours…'
      : `${fmt(data.hours, 0)} h · ${fmt(data.expenses, 0)} € · ${fmt(data.velocity, 1)} tokens/j (${period})`;
  const setRange = (key, value) => onMetricsRangeChange && onMetricsRangeChange((prev) => ({ ...prev, [key]: value }));

  return (
    <section className="rounded-xl border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Chiffres du cercle</span>
        <span className="ml-auto text-xs text-slate-500 truncate">{line}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-[11px] text-slate-500">
              Du
              <input
                type="date"
                value={metricsRange?.start || ''}
                onChange={(e) => setRange('start', e.target.value)}
                className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="text-[11px] text-slate-500">
              au
              <input
                type="date"
                value={metricsRange?.end || ''}
                onChange={(e) => setRange('end', e.target.value)}
                className="mt-0.5 block rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() => currentSlotRange && onMetricsRangeChange({ start: currentSlotRange.start, end: currentSlotRange.end })}
              disabled={!currentSlotRange || isUsingSlotRange}
              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Slot en cours
            </button>
          </div>
          {!metricsRangeValid && metricsRange?.start && metricsRange?.end && (
            <p className="text-xs text-rose-600">La date de fin doit être après la date de début.</p>
          )}
          {error && <p className="text-xs text-rose-600">{error}</p>}
          {data.projects > 0 && !loading && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Temps', value: `${fmt(data.hours, 1)} h`, hint: `${data.covered}/${data.projects} projets suivis` },
                { label: 'Dépenses', value: `${fmt(data.expenses, 0)} €`, hint: 'Achats des projets' },
                { label: 'Vélocité', value: `${fmt(data.velocity, 1)}`, hint: 'tokens par jour, moyenne' },
              ].map((card) => (
                <div key={card.label} className="rounded-lg bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{card.label}</p>
                  <p className="text-base font-semibold text-slate-800">{card.value}</p>
                  <p className="text-[11px] text-slate-500">{card.hint}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default CircleMetrics;
